import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Eye, EyeOff, Pin, PinOff, Search, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Navigation from "../components/Navigation";
import AdminSubNav from "../components/AdminSubNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTopics({ user, setUser }) {
  const [topics, setTopics] = useState([]);
  const [publicLimit, setPublicLimit] = useState(30);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busySlug, setBusySlug] = useState(null);

  const token = localStorage.getItem("access_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTopics = async () => {
    try {
      const resp = await axios.get(`${API}/admin/topics`, { headers });
      setTopics(resp.data.topics || []);
      setPublicLimit(resp.data.public_limit || 30);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error loading topics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTopics(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const curate = async (slug, action) => {
    setBusySlug(slug);
    try {
      await axios.post(`${API}/admin/topics/curation`, { slug, action }, { headers });
      await fetchTopics();
      toast.success(
        action === "hide" ? "Topic hidden from the public page"
          : action === "unhide" ? "Topic visible again"
            : action === "pin" ? "Topic pinned to the top"
              : "Topic unpinned",
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error updating topic");
    } finally {
      setBusySlug(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return topics;
    return topics.filter((t) => t.name.toLowerCase().includes(q));
  }, [topics, query]);

  const pinnedCount = topics.filter((t) => t.pinned).length;
  const hiddenCount = topics.filter((t) => t.hidden).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} setUser={setUser} />
      <AdminSubNav user={user} activeTab="topics" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }} data-testid="admin-topics-title">
              Topic Curation
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              The public <span className="font-medium">/topics</span> page shows the top {publicLimit} tags — pinned first, hidden excluded.
              Hide junk tags (e.g. "test") and pin your priority topics. Changes apply to the page and the sitemap immediately.
            </p>
          </div>
          <div className="flex gap-3 text-sm text-slate-500">
            <span className="rounded-full bg-orange-50 border border-orange-200 text-orange-700 px-3 py-1" data-testid="admin-topics-pinned-count">{pinnedCount} pinned</span>
            <span className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1" data-testid="admin-topics-hidden-count">{hiddenCount} hidden</span>
          </div>
        </div>

        <div className="relative mb-4 max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter tags…"
            className="pl-9"
            data-testid="admin-topics-search"
          />
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500">Loading topics…</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-topics-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Topic</th>
                  <th className="px-4 py-3">Documents</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((topic) => (
                  <tr key={topic.slug} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`admin-topic-row-${topic.slug}`}>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 capitalize text-slate-800">
                        <Tag className="w-3.5 h-3.5 text-orange-400" />{topic.name}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">{topic.count}</td>
                    <td className="px-4 py-2.5">
                      {topic.hidden ? (
                        <span className="rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 text-xs">Hidden</span>
                      ) : topic.pinned ? (
                        <span className="rounded-full bg-orange-50 border border-orange-200 text-orange-700 px-2 py-0.5 text-xs">Pinned</span>
                      ) : (
                        <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs">Visible</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8"
                          disabled={busySlug === topic.slug || topic.hidden}
                          onClick={() => curate(topic.slug, topic.pinned ? "unpin" : "pin")}
                          data-testid={`admin-topic-pin-${topic.slug}`}
                        >
                          {topic.pinned ? <PinOff className="w-3.5 h-3.5 mr-1" /> : <Pin className="w-3.5 h-3.5 mr-1" />}
                          {topic.pinned ? "Unpin" : "Pin"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className={`h-8 ${topic.hidden ? "" : "text-red-600 hover:text-red-700"}`}
                          disabled={busySlug === topic.slug}
                          onClick={() => curate(topic.slug, topic.hidden ? "unhide" : "hide")}
                          data-testid={`admin-topic-hide-${topic.slug}`}
                        >
                          {topic.hidden ? <Eye className="w-3.5 h-3.5 mr-1" /> : <EyeOff className="w-3.5 h-3.5 mr-1" />}
                          {topic.hidden ? "Unhide" : "Hide"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No tags match your filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
